import { FormControl, Grid, IconButton, InputAdornment, InputLabel, Select, TextField, Autocomplete
    , Typography, Stack, Box, Checkbox, FormControlLabel, Tooltip  } from '@mui/material';
import { Search as IconSearch, Edit as IconEdit, AddCircle as IconAddCircle } from "@mui/icons-material";
import comprobanteService from "../services/comprobanteService";
import * as catalogoService from "../services/catalogoService";
import * as filialService from "../services/filialService";
import * as personaService from "../services/personaService";
import * as serieService from "../services/serieService";
import { obtenerUsuario, getUsuarioByCodigo } from "../services/authService";
import { obtenerPerfil } from "../services/usuarioService";
import moment from 'moment';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { init, setEntity, setData, setValidationMessage, activeValidations
  , setListTipo, setListMoneda, setListEstadoPago, setListEstado, setListSerie, defaultReceipt} from "../redux/receiptSlice";
import { FormHelperText as HelperText } from '@mui/material';
import PersonSearch from './PersonSearch';
import MontoTextField from './common/MontoTextField';

const Receipt = (props) => {
    const isReadOnly = props.isReadOnly;
    const dispatch = useDispatch();
    const sliceKey = props.sliceKey;
    const inputRef = useRef(null);
    const pagadorInputRef = useRef(null);
    const params = useParams();

    // El comprobante se puede abrir como pagina o embebido en un dialogo. En el
    // dialogo no hay parametro de ruta, asi que el identificador llega por prop.
    const idComprobante = props.idComprobante != null ? props.idComprobante : params.id;

    useEffect(() => {
        dispatch(init({ sliceKey }));
    }, [dispatch, sliceKey]);

    useEffect(() => {
        if (idComprobante == 0 && !isReadOnly) {
            const timer = setTimeout(() => {
                pagadorInputRef.current?.focus();
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [idComprobante, isReadOnly]);

    useEffect(() => {
        if (props.autoFocus) {
        const timer = setTimeout(() => inputRef.current?.focus(), 150);
        return () => clearTimeout(timer);
        }
    }, [props.autoFocus]);

    const entity = useSelector((state) => state.receipt[sliceKey]?.entity);
    const listTipo = useSelector((state) => state.receipt[sliceKey]?.listTipo);
    const listMoneda = useSelector((state) => state.receipt[sliceKey]?.listMoneda);
    const listEstadoPago = useSelector((state) => state.receipt[sliceKey]?.listEstadoPago);
    const listEstado = useSelector((state) => state.receipt[sliceKey]?.listEstado);
    const listSerie = useSelector((state) => state.receipt[sliceKey]?.listSerie);
    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const [listFilial, setListFilial] = useState([]);
    const validate = useSelector((state) => state.receipt[sliceKey]?.validationActive);
    const [selectIdPersona, setSelectIdPersona] = useState(null);
    const fechaIngresoMinimo = new Date(2010, 1, 1);

    const seriesDelTipo = (listSerie || []).filter((row) => row.idTipo == entity?.idTipo);

    const [selectIdTipoPersona, setSelectIdTipoPersona] = useState("cobrador");
    const [openSearchPersona, setOpenSearchPersona] = useState(false)
    const openCloseSearchPersona = () => { setOpenSearchPersona(!openSearchPersona); }

    var validation = {
        fecha: {
            error: Date.parse(entity?.fecha) < fechaIngresoMinimo,
            message: "Debe ser una fecha mayor al " + fechaIngresoMinimo.getFullYear(),
        },
        idTipo: {
            error: entity?.idTipo == 0 || entity?.idTipo == null,
            message: "Falta ingresar el tipo de comprobante",
        },
        serie: {
            error: entity?.serie == null || entity?.serie === "",
            message: "Falta la serie del comprobante",
        },
        numero: {
            error: entity?.numero != null && entity?.numero !== "" && isNaN(parseInt(entity?.numero)),
            message: "El numero del comprobante debe ser numerico",
        },
        // "No Persona" es un id valido (0), asi que se valida por el nombre.
        idCobrador: {
            error: entity?.cobrador == null || entity?.cobrador == "",
            message: "Ingrese el cobrador del comprobante",
        },
        idPagador: {
            error: entity?.pagador == null || entity?.pagador == "",
            message: "Ingrese el pagador del comprobante",
        },
    }

    const setCobradorUsuarioActual = useCallback(async () => {
        try {
            const perfil = await obtenerPerfil();
            if (perfil) {
                const idPersona = perfil.idPersona ?? perfil.IdPersona ?? 0;
                let nombres = (perfil.nombres ?? perfil.Nombres ?? "").trim();

                if (!nombres && idPersona > 0) {
                    const persona = await personaService.getPersonaById(idPersona).catch(() => null);
                    if (persona?.nombres) {
                        nombres = persona.nombres.trim();
                    }
                }

                if (!nombres) {
                    nombres = (perfil.codigo ?? perfil.Codigo ?? obtenerUsuario() ?? "").trim();
                }

                dispatch(setData({ sliceKey: sliceKey, name: "idCobrador", value: idPersona }));
                dispatch(setData({ sliceKey: sliceKey, name: "cobrador", value: nombres }));
                return;
            }
        } catch (err) {
            console.log("Err obtenerPerfil en Receipt: ", err);
        }

        const codigo = obtenerUsuario();
        if (!codigo) return;

        const usuario = await getUsuarioByCodigo(codigo)
            .catch((err) => { console.log("Err: ", err); });

        if (usuario) {
            const idPer = usuario.idPersona ?? usuario.IdPersona ?? 0;
            let nombres = "";
            if (idPer > 0) {
                const persona = await personaService.getPersonaById(idPer)
                    .catch((err) => { console.log("Err: ", err); });
                if (persona?.nombres) {
                    nombres = persona.nombres.trim();
                }
            }
            dispatch(setData({ sliceKey: sliceKey, name: "idCobrador", value: idPer }));
            dispatch(setData({ sliceKey: sliceKey, name: "cobrador", value: nombres || usuario.codigo || codigo }));
        }
    }, [sliceKey, dispatch]);

    const fetchEntity = useCallback(async () => {
        if (idComprobante == 0){
            // Comprobante nuevo: pagador en blanco
            const inicial = {
                ...defaultReceipt,
                ...(props.prefill || {}),
                idPagador: 0,
                pagador: "",
                dniPagador: "",
            };

            dispatch(setEntity({sliceKey: sliceKey, data: inicial}));
            setCobradorUsuarioActual();
            return;
        }

        const response = await comprobanteService.getComprobanteView(idComprobante)
            .catch((err) => {
                console.log("Err: ", err);
            });

        if (!response?.data) return;

        dispatch(setEntity({sliceKey: sliceKey, data: response.data.data}));
    }, [idComprobante, props.prefill, sliceKey, dispatch, setCobradorUsuarioActual])
    const fetchListTipo = async () => {
        const data = await catalogoService.getCatalogosByPadre(140)
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListTipo({sliceKey: sliceKey, data: data}));
    }
    const fetchListMoneda = async () => {
        const data = await catalogoService.getCatalogosByPadre(136)
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListMoneda({sliceKey: sliceKey, data: data}));
    }
    const fetchListEstadoPago = async () => {
        const data = await catalogoService.getCatalogosByPadre(143)
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListEstadoPago({sliceKey: sliceKey, data: data}));
    }
    const fetchListEstado = async () => {
        const data = await catalogoService.getCatalogosByPadre(1)
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListEstado({sliceKey: sliceKey, data: data}));
    }
    const fetchListSerie = async () => {
        const data = await serieService.getsSerie()
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListSerie({sliceKey: sliceKey, data: data}));
    }
    useEffect(() => {
        const primeraSerie = (listSerie || []).find((row) => row.idTipo == entity?.idTipo);

        if (primeraSerie && (!entity?.serie || entity.serie === "")) {
            dispatch(setData({sliceKey, name: "serie", value: primeraSerie.codigo}));
        }
    }, [listSerie, entity?.idTipo]);
    useEffect(() => {
        if (idComprobante == 0 && entity && (!entity.idCobrador || entity.idCobrador === 0 || !entity.cobrador)) {
            setCobradorUsuarioActual();
        }
    }, [idComprobante, entity?.idCobrador, entity?.cobrador, setCobradorUsuarioActual]);
    const fetchListFilial = useCallback(async () => {
        const data = await filialService.getFilialesList().catch(() => []);
        const lista = filialService.soloFilialDelUsuario(data, idFilialUsuario);
        setListFilial(lista);
        if (idFilialUsuario > 0 && (!idComprobante || idComprobante == 0)) {
            dispatch(setData({ sliceKey: sliceKey, name: "idFilial", value: idFilialUsuario }));
        }
    }, [idFilialUsuario, idComprobante, sliceKey, dispatch]);

    useEffect(() => {
        fetchListFilial();
    }, [fetchListFilial]);

    useEffect(() => {
        fetchEntity();
        fetchListTipo();
        fetchListMoneda();
        fetchListEstadoPago();
        fetchListEstado();
        fetchListSerie();
        dispatch(setValidationMessage({sliceKey: sliceKey, message: messagesValidation()}));
    }, []);
    useEffect(() => {
        if (listTipo?.length > 0 && (!entity?.idTipo || entity.idTipo === 0)) {
            dispatch(setData({sliceKey, name: "idTipo", value: 141}));
        }
    }, [listTipo, entity]);
    useEffect(() => {
        if (listMoneda?.length > 0 && (!entity?.idMoneda || entity.idMoneda === 0)) {
            dispatch(setData({sliceKey, name: "idMoneda", value: 137}));
        }
    }, [listMoneda, entity]);
    useEffect(() => {
        if (listEstadoPago?.length > 0 && (!entity?.idEstadoPago || entity.idEstadoPago === 0)) {
            dispatch(setData({sliceKey, name: "idEstadoPago", value: 144}));
        }
    }, [listEstadoPago, entity]);
    // En soles el tipo de cambio es 1 y se deja listo para no guardarlo en cero
    // cuando el usuario no lo edita.
    useEffect(() => {
        if (!entity?.idMoneda) return;
        if (parseFloat(entity?.tipoCambio || 0) > 0) return;

        const moneda = listMoneda?.find((row) => row.id == entity.idMoneda);
        if (!moneda) return;

        if ((moneda.codigo || "PEN") === "PEN") {
            dispatch(setData({sliceKey, name: "tipoCambio", value: 1.0000}));
        }
    }, [entity?.idMoneda, entity?.tipoCambio, listMoneda]);
    useEffect(() => {
        // El comprobante se registra siempre activo; el estado se cambia desde la busqueda.
        if (!entity?.idEstado || entity.idEstado === 0) {
            dispatch(setData({sliceKey, name: "idEstado", value: 2}));
        }
    }, [entity?.idEstado]);

    const fetchBuscarCobradorByDni = async (dni) => {
        const data = await personaService.getsPersonaDocumentoByTipoYNumeroView(6, dni)
            .catch((err) => {
                console.log("Err: ", err)
            });
        console.log(data)

        if (!data) {
            console.log("No se encontro persona con el DNI " + dni);
            return;
        }

        dispatch(setData({sliceKey: sliceKey, name: "idCobrador", value: data.idPersona}));
        dispatch(setData({sliceKey: sliceKey, name: "cobrador", value: data.nombres}));
    }
    const fetchBuscarPagadorByDni = async (dni) => {
        const data = await personaService.getsPersonaDocumentoByTipoYNumeroView(6, dni)
            .catch((err) => {
                console.log("Err: ", err)
            });

        if (!data) {
            console.log("No se encontro persona con el DNI " + dni);
            return;
        }

        dispatch(setData({sliceKey: sliceKey, name: "idPagador", value: data.idPersona}));
        dispatch(setData({sliceKey: sliceKey, name: "pagador", value: data.nombres}));
    }
    const setSelectSearchCobrador = (select) => {
        dispatch(setData({sliceKey: sliceKey, name: "idCobrador", value: select.idPersona}));
        dispatch(setData({sliceKey: sliceKey, name: "dniCobrador", value: select.numeroDocumento || ""}));
        dispatch(setData({sliceKey: sliceKey, name: "cobrador", value: select.nombres}));
    }
    const setSelectSearchPagador = (select) => {
        dispatch(setData({sliceKey: sliceKey, name: "idPagador", value: select.idPersona}));
        dispatch(setData({sliceKey: sliceKey, name: "dniPagador", value: select.numeroDocumento || ""}));
        dispatch(setData({sliceKey: sliceKey, name: "pagador", value: select.nombres}));
    }
    const fetchObtenerTipoCambio = async (moneda) => {
        if (moneda !== "PEN") {
            const response = await comprobanteService.getTipoCambio(moneda)
                .catch((err) => {
                    console.log("Err: ", err)
                });
            console.log(response)

            // La API externa devuelve [{date, base, quote, rate}]
            const tipoCambio = Array.isArray(response?.data) ? response.data[0]?.rate : response?.data?.rates?.PEN;

            if (tipoCambio) {
                dispatch(setData({sliceKey: sliceKey, name: "tipoCambio", value: tipoCambio}));
            }
        }
        else {
            dispatch(setData({sliceKey: sliceKey, name: "tipoCambio", value: 1.0000}));
        }
    }
    //https://api.frankfurter.dev/v2/rates?base=USD&quotes=PEN

    useEffect(() => {
        dispatch(setValidationMessage({sliceKey: sliceKey, message: messagesValidation()}));
    }, [entity])

    var setDataEntity = e => {
        dispatch(setData({sliceKey: sliceKey, name: e.target.name, value: e.target.value}));
    }

    const messagesValidation = () => {
        var messages = [];

        Object.keys(validation).forEach(function (key) {
            if (validation[key].error) messages.push(validation[key].message)
        })

        return messages;
    }

    return (
        <Grid container>
            {/* {JSON.stringify(validation)}
            {JSON.stringify(validate)} */}
            {/* {JSON.stringify(entity)} */}
            <Grid item xs={12}>
                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        Datos Básicos
                    </Typography>
                </Box>
            </Grid>
            <Grid container spacing={1} padding={"4px 0px 0px 0px"}>
                <Grid item xs={6} sm={2}>
                    <FormControl required fullWidth>
                        <InputLabel htmlFor="input-filial">Filial</InputLabel>
                        <Select
                            native
                            label="Filial"
                            value={entity?.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 0)}
                            onChange={(event) => dispatch(setData({ sliceKey: sliceKey, name: "idFilial", value: parseInt(event.target.value) || 0 }))}
                            inputProps={{ name: "idFilial", id: "input-filial" }}
                            disabled={isReadOnly || idFilialUsuario > 0}
                            size="small"
                        >
                            {idFilialUsuario <= 0 && <option key={0} value={0}>{"(Seleccione)"}</option>}
                            {listFilial.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion || row.nombre}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={2}>
                    <TextField
                        required
                        name="fecha"
                        label="Fecha"
                        fullWidth
                        type="date"
                        //defaultValue={moment()}
                        value={moment(entity?.fecha).format('yyyy-MM-DD')}
                        onChange={setDataEntity}
                        error={validate?.fecha && validation.fecha.error}
                        helperText={validate?.fecha && validation.fecha.error ? validation.fecha.message : ""}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={6} sm={2}>
                    <FormControl required fullWidth error={validate?.idTipo && validation.idTipo.error}>
                        <InputLabel htmlFor="input-modelo" shrink>Tipo</InputLabel>
                        <Select
                            native
                            label="Tipo"
                            value={entity?.idTipo || 0}
                            onChange={(event) => {
                                var id = event.nativeEvent.target.selectedIndex;
                                
                                dispatch(setData({sliceKey: sliceKey, name: "idTipo", value: event.target.value}))
                                dispatch(setData({sliceKey: sliceKey, name: "tipo", value: event.nativeEvent.target[id].text}))
                            }}
                            inputProps={{
                                name: 'idTipo',
                                id: 'input-tipo',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={0} value={0}>{"(Seleccione)"}</option>
                            {listTipo?.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion}</option>
                            ))}
                        </Select>
                        <HelperText>
                            {validate?.idTipo && validation.idTipo.error ? validation.idTipo.message : ""}
                        </HelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={3} sm={1.5}>
                    <Autocomplete
                        freeSolo
                        disabled={isReadOnly}
                        options={seriesDelTipo}
                        getOptionLabel={(option) => typeof option === "string" ? option : option.codigo}
                        inputValue={entity?.serie || ""}
                        onInputChange={(event, value) => {
                            dispatch(setData({sliceKey: sliceKey, name: "serie", value: value.toUpperCase()}))
                        }}
                        onChange={(event, value) => {
                            const serie = typeof value === "string" ? value : value?.codigo;
                            if (serie) dispatch(setData({sliceKey: sliceKey, name: "serie", value: serie}))
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                required
                                name="serie"
                                id="input-serie"
                                label="Serie"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                error={validate?.serie && validation.serie.error}
                                helperText={validate?.serie && validation.serie.error ? validation.serie.message : ""}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={3} sm={1.5}>
                    <TextField
                        required
                        type="number"
                        name="numero"
                        label="Numero"
                        fullWidth
                        value={entity?.numero == null || entity?.numero === "" ? "" : entity.numero}
                        onChange={(event) => {
                            dispatch(setData({sliceKey: sliceKey, name: "numero", value: event.target.value}))
                        }}
                        error={validate?.numero && validation.numero.error}
                        helperText={validate?.numero && validation.numero.error ? validation.numero.message : ""}
                        disabled={isReadOnly}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 1, style: { textAlign: "right" } }}
                    />
                </Grid>
                <Grid item xs={6} sm={1.5}>
                    <FormControl required fullWidth>
                        <InputLabel htmlFor="input-moneda" shrink>Moneda</InputLabel>
                        <Select
                            native
                            label="Moneda"
                            value={entity?.idMoneda || 0}
                            onChange={(event) => {
                                const moneda = listMoneda?.find((row) => row.id == event.target.value);

                                dispatch(setData({sliceKey: sliceKey, name: "idMoneda", value: event.target.value}))
                                dispatch(setData({sliceKey: sliceKey, name: "moneda", value: moneda?.descripcion || ""}))

                                fetchObtenerTipoCambio(moneda?.codigo || "PEN")
                            }}
                            inputProps={{
                                name: 'idMoneda',
                                id: 'input-moneda',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={0} value={0}>{"(Seleccione)"}</option>
                            {listMoneda?.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={1.5}>
                    <MontoTextField
                        required
                        name="tipoCambio"
                        label="T. Cambio"
                        fullWidth
                        value={entity?.tipoCambio ?? ""}
                        onChange={(event) => {
                            dispatch(setData({sliceKey: sliceKey, name: "tipoCambio", value: event.target.value}))
                        }}
                        disabled={isReadOnly}
                        size="small"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        name="cobrador"
                        label="Cobrador"
                        fullWidth
                        value={entity?.cobrador || ""}
                        size="small"
                        disabled={isReadOnly}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Buscar cobrador" arrow>
                                        <IconButton aria-label="buscar cobrador" size="small" edge="end" disabled={isReadOnly} onClick={() => {
                                            setSelectIdTipoPersona("cobrador")
                                            openCloseSearchPersona()
                                        }}>
                                            <IconSearch fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        inputRef={pagadorInputRef}
                        name="pagador"
                        label="Pagador"
                        fullWidth
                        value={entity?.pagador || ""}
                        size="small"
                        disabled={isReadOnly}
                        InputLabelProps={{ shrink: true }}
                        onClick={() => {
                            if (!isReadOnly) {
                                setSelectIdTipoPersona("pagador");
                                openCloseSearchPersona();
                            }
                        }}
                        onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && !isReadOnly) {
                                e.preventDefault();
                                setSelectIdTipoPersona("pagador");
                                openCloseSearchPersona();
                            }
                        }}
                        inputProps={{
                            style: { cursor: isReadOnly ? "default" : "pointer" }
                        }}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Buscar pagador" arrow>
                                        <IconButton aria-label="buscar pagador" size="small" edge="end" disabled={isReadOnly} onClick={() => {
                                            setSelectIdTipoPersona("pagador")
                                            openCloseSearchPersona()
                                        }}>
                                            <IconSearch fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                {/* Observaciones, Total y Saldo se muestran debajo del detalle de pagos. */}
                {/* <Grid item xs={12} sm={5}>
                    <Box display="flex" alignItems="center" gap={0}>
                        <FormControl required fullWidth>
                            <Autocomplete
                                name="idDestreza"
                                value={listDestreza.find(x => x.id === entity.idDestreza) || null}
                                inputValue={entity.destreza || ""}
                                onInputChange={(event, newValue) => {
                                    dispatch(setData({ sliceKey: sliceKey, name: "destreza", value: newValue || "" }))

                                    const match = listDestreza.find(
                                        x => x.descripcion.toLowerCase() === (newValue || "").toLowerCase()
                                    )

                                    if (!match) {
                                        dispatch(setData({ sliceKey: sliceKey, name: "idDestreza", value: 0 }))
                                    }
                                }}
                                onChange={(event, newValue) => {
                                    dispatch(setData({sliceKey: sliceKey, name: "idDestreza", value: newValue?.id ?? 0}))
                                    dispatch(setData({sliceKey: sliceKey, name: "destreza", value: newValue?.descripcion ?? ""}))
                                    setSelectIdPersona(newValue?.id ?? 0)
                                }}
                                options={listDestreza}
                                autoHighlight
                                getOptionLabel={(option) => option.descripcion || ""}
                                isOptionEqualToValue ={(option, value) => option.id === value.id}
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props} key={option.id}>
                                        {option.descripcion}
                                        </li>
                                    );
                                    }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Destreza"
                                        variant="outlined"
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: 'new-password',
                                        }}
                                    />
                                )}
                                size="small"
                            />
                        </FormControl>
                        <IconButton aria-label="editar destreza" size='small' onClick={() => {
                            setSelectIdPersona(entity.idDestreza)
                            openClosePersonSearch()
                            }}>
                            {entity.idDestreza > 0 ? <IconEdit /> : <IconAddCircle />}
                        </IconButton>
                    </Box>
                </Grid> */}
            </Grid>
            <PersonSearch open={openSearchPersona} openClose={openCloseSearchPersona} setSelection={selectIdTipoPersona == "cobrador" ? setSelectSearchCobrador : setSelectSearchPagador} />
        </Grid>
    );
}
export default Receipt;
